import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, getToken } from "@/lib/api";

import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Send, MessageCircle, Paperclip, X, Image as ImageIcon, Loader2, Wifi, WifiOff } from "lucide-react";
import { GuardedText } from "@/components/GuardedText";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const Messages = () => {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [typingFrom, setTypingFrom] = useState(null);
  const [wsState, setWsState] = useState("disconnected"); // disconnected | connecting | open
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimer = useRef(null);
  const activeRef = useRef(null);
  activeRef.current = active;

  const loadConvs = useCallback(async () => {
    const { data } = await api.get("/conversations");
    setConvs(data);
    const cId = params.get("c");
    if (cId) {
      const c = data.find((x) => x.conversation_id === cId);
      if (c) setActive(c);
    } else if (data.length > 0 && !activeRef.current) {
      setActive(data[0]);
    }
  }, [params]);

  useEffect(() => { if (user) loadConvs(); }, [user, loadConvs]);

  // Open a WebSocket per active conversation; falls back to a slow poll if WS fails.
  useEffect(() => {
    if (!active || !user) return undefined;
    let cancelled = false;
    let pollTimer = null;
    const tok = getToken();

    const loadMessages = async () => {
      const { data } = await api.get(`/conversations/${active.conversation_id}/messages`);
      if (cancelled) return;
      setMessages(data.messages);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
      // Mark conversation as read (server-side + locally)
      api.post(`/conversations/${active.conversation_id}/read`).catch(() => {});
      setConvs((arr) => arr.map((x) => x.conversation_id === active.conversation_id ? { ...x, unread_count: 0 } : x));
    };
    loadMessages();

    const wsBase = BACKEND_URL.replace(/^http/, "ws");
    const wsUrl = `${wsBase}/api/ws/conversations/${active.conversation_id}?token=${encodeURIComponent(tok || "")}`;
    let ws;
    try {
      setWsState("connecting");
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;
    } catch {
      setWsState("disconnected");
    }
    if (ws) {
      ws.onopen = () => setWsState("open");
      ws.onclose = () => { setWsState("disconnected"); wsRef.current = null; };
      ws.onerror = () => setWsState("disconnected");
      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (data.type === "message" && data.message) {
            setMessages((m) => m.some((x) => x.message_id === data.message.message_id) ? m : [...m, data.message]);
            setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
            if (data.message.sender_id !== user.user_id) {
              api.post(`/conversations/${active.conversation_id}/read`).catch(() => {});
            }
          } else if (data.type === "typing" && data.user_id !== user.user_id) {
            setTypingFrom(data.name || "Someone");
            window.clearTimeout(typingTimer.current);
            typingTimer.current = window.setTimeout(() => setTypingFrom(null), 3000);
          }
        } catch { /* ignore */ }
      };
    }

    // Polling fallback: refresh every 10s in case WS drops
    pollTimer = setInterval(() => {
      if (wsRef.current?.readyState !== WebSocket.OPEN) loadMessages();
    }, 10000);

    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
      if (ws) { try { ws.close(); } catch { /* noop */ } }
      wsRef.current = null;
      setTypingFrom(null);
    };
  }, [active, user]);

  const emitTyping = () => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify({ type: "typing" })); } catch { /* noop */ }
    }
  };

  const onTextChange = (e) => {
    setText(e.target.value);
    emitTyping();
  };

  const onPickFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachments((arr) => [...arr, { path: data.path, name: file.name, content_type: file.type }]);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const send = async (e) => {
    e?.preventDefault?.();
    if (!active) return;
    const body = text.trim();
    if (!body && attachments.length === 0) return;
    setSending(true);
    try {
      await api.post(`/conversations/${active.conversation_id}/messages`, { text: body, attachments });
      setText("");
      setAttachments([]);
      // Optimistic: WebSocket will deliver the canonical message back.
      // If WS isn't open, the polling fallback or `loadMessages` next tick will pick it up.
      // Also re-fetch conv list so last_message + ordering update on the sidebar.
      loadConvs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send message");
    } finally {
      setSending(false);
    }
  };

  if (!user) return null;

  return (
    <div className="container-narrow py-8">
      <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
        <h1 className="font-display text-3xl">Messages</h1>
        {active && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground" data-testid="ws-state">
            {wsState === "open" ? <><Wifi className="w-3 h-3 text-[#4A5D23]"/> Live</> : <><WifiOff className="w-3 h-3"/> Sync</>}
          </div>
        )}
      </div>
      <div className="bg-white border border-black/10 rounded-lg grid grid-cols-1 md:grid-cols-3 h-[70vh] overflow-hidden">
        {/* Sidebar */}
        <div className="border-r border-black/10 overflow-y-auto" data-testid="messages-sidebar">
          {convs.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">
              <MessageCircle className="w-8 h-8 mx-auto mb-2"/>
              No conversations yet
            </div>
          ) : convs.map((c) => (
            <button
              key={c.conversation_id}
              onClick={() => { setActive(c); setParams({ c: c.conversation_id }); }}
              data-testid={`conv-item-${c.conversation_id}`}
              className={`w-full text-left p-4 border-b border-black/5 hover:bg-[#F3EFE9] flex gap-3 ${active?.conversation_id === c.conversation_id ? "bg-[#F3EFE9]" : ""}`}
            >
              <Avatar className="w-10 h-10 shrink-0">
                <AvatarImage src={c.other_user?.picture || undefined}/>
                <AvatarFallback className="bg-[#233348] text-white text-sm">{c.other_user?.name?.slice(0,1)}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate flex items-center gap-1.5">
                  {c.other_user?.name || "User"}
                  {c.unread_count > 0 && (
                    <Badge className="bg-[#C05A3C] text-white text-[10px] h-4 px-1.5 ml-auto" data-testid={`unread-${c.conversation_id}`}>
                      {c.unread_count}
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground truncate">{c.listing_title}</div>
                <div className={`text-xs truncate ${c.unread_count > 0 ? "text-[#1A2024] font-semibold" : "text-muted-foreground/80"}`}>{c.last_message || "—"}</div>
              </div>
            </button>
          ))}
        </div>

        {/* Conversation */}
        <div className="md:col-span-2 flex flex-col">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">Select a conversation</div>
          ) : (
            <>
              <div className="p-4 border-b border-black/5 flex items-center gap-3">
                <Avatar className="w-9 h-9">
                  <AvatarImage src={active.other_user?.picture || undefined}/>
                  <AvatarFallback className="bg-[#233348] text-white text-sm">{active.other_user?.name?.slice(0,1)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">{active.other_user?.name}</div>
                  <Link to={`/listing/${active.listing_id}`} className="text-xs text-muted-foreground hover:text-[#C05A3C]">About: {active.listing_title}</Link>
                </div>
                {active.other_user?.user_id && (
                  <Link
                    to={`/complaints/new?respondent_id=${active.other_user.user_id}&listing_id=${active.listing_id}`}
                    className="text-xs text-muted-foreground hover:text-[#C05A3C] underline"
                    data-testid="chat-report-user"
                  >
                    Report user
                  </Link>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FAF8F5]" data-testid="messages-list">
                {messages.map((m) => {
                  const mine = m.sender_id === user.user_id;
                  return (
                    <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.message_id}`}>
                      <div className={`max-w-[75%] px-4 py-2 rounded-lg text-sm ${mine ? "bg-[#C05A3C] text-white rounded-br-sm" : "bg-white border border-black/10 rounded-bl-sm"}`}>
                        {m.text && <div className="whitespace-pre-wrap leading-relaxed"><GuardedText text={m.text} /></div>}
                        {m.attachments?.length > 0 && (
                          <div className={`mt-2 grid gap-2 ${m.attachments.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
                            {m.attachments.map((a, i) => {
                              const url = `${BACKEND_URL}/api/files/${a.path}`;
                              const isImage = (a.content_type || "").startsWith("image/");
                              return isImage ? (
                                <a key={i} href={url} target="_blank" rel="noreferrer">
                                  <img src={url} alt={a.name || "attachment"} className="rounded-md max-h-48 object-cover"/>
                                </a>
                              ) : (
                                <a key={i} href={url} target="_blank" rel="noreferrer" className={`text-xs underline flex items-center gap-1 ${mine ? "text-white/90" : "text-[#C05A3C]"}`}>
                                  <Paperclip className="w-3 h-3"/> {a.name || "attachment"}
                                </a>
                              );
                            })}
                          </div>
                        )}
                        <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-muted-foreground"}`}>
                          {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {typingFrom && (
                  <div className="flex justify-start" data-testid="typing-indicator">
                    <div className="bg-white border border-black/10 rounded-lg rounded-bl-sm px-3 py-2 text-xs text-muted-foreground italic">
                      {typingFrom} is typing…
                    </div>
                  </div>
                )}
                <div ref={endRef}/>
              </div>
              {attachments.length > 0 && (
                <div className="px-3 pt-2 flex flex-wrap gap-2" data-testid="pending-attachments">
                  {attachments.map((a, i) => (
                    <div key={i} className="flex items-center gap-1.5 bg-[#F3EFE9] rounded-md px-2 py-1 text-xs">
                      {a.content_type?.startsWith("image/") ? <ImageIcon className="w-3 h-3"/> : <Paperclip className="w-3 h-3"/>}
                      <span className="truncate max-w-[140px]">{a.name}</span>
                      <button type="button" onClick={() => setAttachments((arr) => arr.filter((_, j) => j !== i))}><X className="w-3 h-3"/></button>
                    </div>
                  ))}
                </div>
              )}
              <form onSubmit={send} className="p-3 border-t border-black/5 flex gap-2 items-center">
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="p-2 rounded-md hover:bg-black/5 text-muted-foreground" data-testid="chat-attach-btn">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Paperclip className="w-4 h-4"/>}
                </button>
                <input ref={fileRef} type="file" hidden accept="image/*,application/pdf" onChange={(e) => onPickFile(e.target.files?.[0])} data-testid="chat-file-input"/>
                <Input data-testid="message-input" value={text} onChange={onTextChange} placeholder="Write a message…"/>
                <Button type="submit" disabled={sending || (!text.trim() && attachments.length === 0)} data-testid="message-send" className="bg-[#C05A3C] hover:bg-[#D46B4E]">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Messages;
